import { useState, useEffect, useRef } from 'react';
import { formatDateTime, formatFileSize } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Upload, Download, Eye, ClockCounterClockwise } from '@phosphor-icons/react';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { FileIcon } from './FileIcon';

export function UploadDocumentDialog({ open, onOpenChange, isReplace, docTypeLabel, uploading, uploadProgress, onUpload }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) setSelectedFile(null);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isReplace ? 'Carica Nuova Versione' : 'Carica Documento'}
                    </DialogTitle>
                    <DialogDescription>
                        {docTypeLabel || 'Documento'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                            selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setSelectedFile(file);
                            }}
                            accept="*/*"
                        />
                        {selectedFile ? (
                            <div className="flex items-center justify-center gap-3">
                                <FileIcon contentType={selectedFile.type} className="w-10 h-10" />
                                <div className="text-left">
                                    <p className="font-medium text-sm">{selectedFile.name}</p>
                                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground">
                                    Clicca o trascina un file qui
                                </p>
                            </>
                        )}
                    </div>

                    {uploading && (
                        <div className="space-y-2">
                            <Progress value={uploadProgress} />
                            <p className="text-xs text-center text-muted-foreground">
                                Caricamento in corso...
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
                        Annulla
                    </Button>
                    <Button onClick={() => onUpload(selectedFile)} disabled={!selectedFile || uploading}>
                        {uploading ? 'Caricamento...' : 'Carica'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function PreviewDocumentDialog({ open, onOpenChange, doc, previewUrl, onDownload }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        {doc?.original_filename}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 min-h-[60vh]">
                    {previewUrl ? (
                        doc?.content_type?.startsWith('image/') ? (
                            <img
                                src={previewUrl}
                                alt={doc?.original_filename}
                                className="max-w-full max-h-[60vh] mx-auto object-contain"
                            />
                        ) : doc?.content_type === 'application/pdf' ? (
                            <iframe
                                src={previewUrl}
                                className="w-full h-[60vh] border rounded"
                                title={doc?.original_filename}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
                                <FileIcon contentType={doc?.content_type} className="w-16 h-16 mb-4" />
                                <p>Anteprima non disponibile per questo tipo di file</p>
                                <Button
                                    variant="outline"
                                    className="mt-4"
                                    onClick={() => doc && onDownload(doc)}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Scarica per visualizzare
                                </Button>
                            </div>
                        )
                    ) : (
                        <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
                            <FileIcon contentType={doc?.content_type} className="w-16 h-16 mb-4" />
                            <p>Anteprima non disponibile</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => doc && onDownload(doc)}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Scarica
                            </Button>
                        </div>
                    )}
                </div>

                {doc && (
                    <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                            <span>{formatFileSize(doc.size)}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDateTime(doc.uploaded_at)}</span>
                            {doc.uploaded_by_name && (
                                <>
                                    <span className="mx-2">•</span>
                                    <span>{doc.uploaded_by_name}</span>
                                </>
                            )}
                        </div>
                        <Button onClick={() => onDownload(doc)}>
                            <Download className="w-4 h-4 mr-2" />
                            Scarica
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function VersionsDialog({ open, onOpenChange, versions, onDownload }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClockCounterClockwise className="w-5 h-5" />
                        Storico Versioni
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="max-h-[60vh]">
                    <div className="space-y-2">
                        {versions.map((doc) => (
                            <div
                                key={doc.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                    doc.is_latest ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                                }`}
                            >
                                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                                    <span className="text-lg font-bold text-muted-foreground">v{doc.version}</span>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                                        {doc.is_latest && (
                                            <Badge variant="default" className="text-xs">Corrente</Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {formatDateTime(doc.uploaded_at)}
                                        {doc.uploaded_by_name && ` • ${doc.uploaded_by_name}`}
                                    </p>
                                </div>

                                <DocumentStatusBadge status={doc.validation_status} />

                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => onDownload(doc)}
                                >
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}

export function DeleteDocumentDialog({ open, onOpenChange, doc, onConfirm }) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Elimina Documento</AlertDialogTitle>
                    <AlertDialogDescription>
                        Sei sicuro di voler eliminare "{doc?.original_filename}"?
                        Questa azione non può essere annullata.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
                        Elimina
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
